<script lang="ts">
  import { _ } from "svelte-i18n";
  import Carousel from "$lib/components/Carousel/Carousel.svelte";
  import Button from "$lib/components/Button/Button.svelte";
  import Container from "$lib/components/Container/Container.svelte";
  import { type Link, CarouselSize } from "$lib/gql/genCms/codegen";
  import cfg from "$lib/config/config";

  import { transformNostoRecsCards } from "$lib/utils/transformNostoRecs";

  import { recommendations } from "$lib/stores/recommendations";
  import type { PageType } from "$lib/stores/recommendations";
  import { regionStore } from "$lib/stores/region";
  import { get } from "svelte/store";
  import { createEventDispatcher } from "svelte";

  const dispatch = createEventDispatcher();

  export let pageType: PageType;
  let recommendationData;
  export let input;
  export let cta: Link = null;
  let cards;
  let show = false;
  export let carouselSize: CarouselSize = CarouselSize.Four;
  export let displayTitle: boolean = true;

  export let ctaPosition = "center";

  let ctaTextLight = cta?.textLight;
  let ctaBgColour = cta?.bgColour;
  let ctaText = cta?.text;
  let ctaHref = cta?.href;

  export let invert = false;

  export let title: string = "";

  function handleAttribution(productId: string) {
    recommendations.setAttribution($recommendations.resultId, productId);
    return true;
  }

  const { productId, variantId, placement, categoryPath, searchTerm } = input;

  const isBrowser = typeof window !== "undefined";

  const regionInfo = regionStore();

  const locale = get(regionInfo).locale;

  let lang = locale;
  lang = lang && lang.split("-")[0];

  $: isBrowser &&
    window.nostojs &&
    window.nostojs(api => {
      recommendations.setUser();
      recommendations.setCart();

      switch (pageType) {
        case "PDP":
          return api
            .defaultSession()
            .setResponseMode("JSON_ORIGINAL")
            .viewProductSku(
              productId?.split("/").slice(-1)[0],
              variantId?.split("/").slice(-1)[0],
            )
            .setPlacements([placement])
            .load()
            .then(
              res =>
                (recommendationData = recommendations.transformRecPlacements(
                  Object.values(res.recommendations)[0],
                  lang,
                )),
            );
        case "PLP":
          return api
            .defaultSession()
            .setResponseMode("JSON_ORIGINAL")
            .viewCategory(categoryPath)
            .setPlacements([placement])
            .load()
            .then(
              res =>
                (recommendationData = recommendations.transformRecPlacements(
                  Object.values(res.recommendations)[0],
                  lang,
                )),
            );

        case "HOME":
          return api
            .defaultSession()
            .setResponseMode("JSON_ORIGINAL")
            .viewFrontPage()
            .setPlacements([placement])
            .load()
            .then(
              res =>
                (recommendationData = recommendations.transformRecPlacements(
                  Object.values(res.recommendations)[0],
                  lang,
                )),
            );

        case "SEARCH":
          return api
            .defaultSession()
            .setResponseMode("JSON_ORIGINAL")
            .viewSearch(searchTerm)
            .setPlacements([placement])
            .load()
            .then(
              res =>
                (recommendationData = recommendations.transformRecPlacements(
                  Object.values(res.recommendations)[0],
                  lang,
                )),
            );

        case "CART":
          return api
            .defaultSession()
            .setResponseMode("JSON_ORIGINAL")
            .viewCart()
            .setPlacements([placement])
            .load()
            .then(
              res =>
                (recommendationData = recommendations.transformRecPlacements(
                  Object.values(res.recommendations)[0],
                  lang,
                )),
            );

        case "LANDING":
          return api
            .defaultSession()
            .setResponseMode("JSON_ORIGINAL")
            .viewFrontPage()
            .setPlacements([placement])
            .load()
            .then(
              res =>
                (recommendationData = recommendations.transformRecPlacements(
                  Object.values(res.recommendations)[0],
                  lang,
                )),
            );

        default:
          break;
      }
    }),
    isBrowser;

  $: recommendations.updatedRecommendations({
    ...recommendationData,
    pageType,
  }),
    recommendationData;

  $: cards = transformNostoRecsCards(recommendationData, lang);

  $: (show = cfg.NOSTO_SHOW_RECS && recommendationData?.products?.length > 0),
    recommendationData;

  $: if (show) {
    dispatch("show");
  }
</script>

<style lang="scss">
  :global(.recommendation-carousel > div > .carousel) {
    padding: 0;
  }
  .recommendation-carousel__cta {
    display: flex;
    &--CENTER {
      justify-content: center;
    }
    &--LEFT {
      justify-content: start !important;
    }
    &--RIGHT {
      justify-content: end !important;
    }
  }
</style>

{#key recommendationData}
  {#if show}
    <div
      class="recommendation-carousel"
      data-placement-id={recommendationData.resultId}
    >
      <Carousel
        recommendations
        id={recommendationData.resultId}
        size={carouselSize}
        title={title !== "" ? title : $_(recommendationData.resultTitle)}
        {displayTitle}
        {cards}
        {handleAttribution}
        quickBuy={true}
        headingElement="h2"
        total={recommendationData.products.length}
        key={recommendationData.resultId}
      >
        {#if cta}
          <Container>
            <div
              class={`recommendation-carousel__cta recommendation-carousel__cta--${ctaPosition}`}
            >
              <Button
                primary
                href={ctaHref}
                label={ctaText}
                primaryInvert={invert}
                theme={ctaBgColour !== "#" && ctaTextLight
                  ? "inverse-on-surface"
                  : ctaBgColour !== "#" && !ctaTextLight
                    ? "on-surface"
                    : invert
                      ? "on-surface"
                      : "inverse-on-surface"}
                hoverTheme={!invert ? "on-surface" : "inverse-on-surface"}
                backgroundColor={ctaBgColour !== "#"
                  ? ctaBgColour
                  : invert && ctaBgColour === "#"
                    ? "on-surface"
                    : "inverse-on-surface"}
                hoverBgColor="transparent"
              />
            </div>
          </Container>
        {/if}
      </Carousel>
    </div>
  {/if}
{/key}
